import api from "../../api/api";

const state = {
    refundable_orders: [],
    refund_order_details: {},
    refund_history_orders: [],
    refund_history_loaded: false,
    refund_fetch_mode: 'pos',
    orderDetailsCache: {},
    loadingDetails: {},
    orderVatRates: {}, // Store VAT rates by order ID
    orderInfoCache: {}, // Store order info by order ID
    // Pagination state
    refundable_orders_pagination: {},
    refund_history_pagination: {},
};

const mutations = {
    SET_REFUNDABLE_ORDERS(state, payload) {
        state.refundable_orders = payload;
    },
    SET_REFUND_ORDER_DETAILS(state, payload) {
        state.refund_order_details = payload;
    },
    CLEAR_REFUND_ORDER_DETAILS(state) {
        state.refund_order_details = {};
    },
    CLEAR_REFUNDABLE_ORDERS(state) {
        state.refundable_orders = [];
    },
    SET_REFUND_HISTORY_ORDERS(state, payload) {
        state.refund_history_orders = payload;
    },
    SET_REFUND_HISTORY_LOADED(state, payload) {
        state.refund_history_loaded = payload;
    },
    CLEAR_REFUND_HISTORY_ORDERS(state) {
        state.refund_history_orders = [];
    },
    SET_REFUND_FETCH_MODE(state, mode) {
        state.refund_fetch_mode = mode;
    },
    SET_ORDER_DETAILS(state, { orderId, details }) {
        state.orderDetailsCache = { ...state.orderDetailsCache, [orderId]: details };
    },
    SET_LOADING_DETAILS(state, { orderId, loading }) {
        state.loadingDetails = { ...state.loadingDetails, [orderId]: loading };
    },
    SET_ORDER_VAT_RATE(state, { orderId, vatRate }) {
        state.orderVatRates = { ...state.orderVatRates, [orderId]: vatRate };
    },
    SET_ORDER_INFO(state, { orderId, orderInfo }) {
        state.orderInfoCache = { ...state.orderInfoCache, [orderId]: orderInfo };
    },
    SET_REFUNDABLE_ORDERS_PAGINATION(state, payload) {
        state.refundable_orders_pagination = payload;
    },
    SET_REFUND_HISTORY_PAGINATION(state, payload) {
        state.refund_history_pagination = payload;
    },
};

const actions = {

    async search_refundable_orders({ commit, state, dispatch }, payload = {}) {
        try {
            // Set global loading state
            await dispatch('Search/setLoading', true, { root: true });
            
            const finalPayload = {
                ...payload,
                mode: state.refund_fetch_mode,
                page: payload.page || 1,
                per_page: payload.per_page || 10,
            };

            const res = await api.instance.post("/api/order/fetch_refund_orders", finalPayload);
            
            // Handle new paginated response format
            if (res.data && res.data.data && res.data.pagination) {
                const data = res.data.data;
                const pagination = res.data.pagination;

                if (payload.only_refunded) {
                    commit("SET_REFUND_HISTORY_ORDERS", data);
                    commit("SET_REFUND_HISTORY_PAGINATION", pagination);
                    commit("SET_REFUND_HISTORY_LOADED", true);
                } else {
                    commit("SET_REFUNDABLE_ORDERS", data);
                    commit("SET_REFUNDABLE_ORDERS_PAGINATION", pagination);
                }
            } else {
                // Fallback for old format (backward compatibility)
                const data = Array.isArray(res.data) ? res.data : [];

                if (payload.only_refunded) {
                    commit("SET_REFUND_HISTORY_ORDERS", data);
                    commit("SET_REFUND_HISTORY_LOADED", true);
                } else {
                    commit("SET_REFUNDABLE_ORDERS", data);
                }
            }
        } catch (error) {
            console.error("Failed to search refundable orders:", error);
        } finally {
            // Always clear global loading state
            await dispatch('Search/setLoading', false, { root: true });
        }
    },

    issue_refund_for_order: async (_context, payload) => {
        try {
            await api.instance.post("/api/refund/issue_refund_for_order", payload);
        } catch (err) {
            console.error("Refund API failed:", err);
            throw err;
        }
    },

    async refresh_single_order_via_search({ commit, state }, invoiceNumber) {
        try {
            const res = await api.instance.post("/api/order/fetch_refund_orders", {
                searchKeyword: invoiceNumber,
                mode: state.refund_fetch_mode
            });

            const results = Array.isArray(res.data) ? res.data : [];
            if (!results.length) return;

            const updatedOrder = results.find(
            o => o.invoice_number === invoiceNumber
            );

            if (!updatedOrder) return;

            const index = state.refundable_orders.findIndex(
            o => o.invoice_number === invoiceNumber
            );

            if (index !== -1) {
            // Replace the item in-place
            const updated = [...state.refundable_orders];
            updated.splice(index, 1, updatedOrder);
            commit("SET_REFUNDABLE_ORDERS", updated);
            }
        } catch (error) {
            console.error("Failed to refresh order via search:", error);
        }
    },

    async fetch_refund_history({ commit, state, dispatch }, payload = {}) {
        try {
            // Set global loading state
            await dispatch('Search/setLoading', true, { root: true });
            
            const finalPayload = {
                only_refunded: true,
                mode: state.refund_fetch_mode,
                page: payload.page || 1,
                per_page: payload.per_page || 10,
            };

            const res = await api.instance.post("/api/order/fetch_refund_orders", finalPayload);
            
            // Handle new paginated response format
            if (res.data && res.data.data && res.data.pagination) {
                const data = res.data.data;
                const pagination = res.data.pagination;
                commit("SET_REFUND_HISTORY_ORDERS", data);
                commit("SET_REFUND_HISTORY_PAGINATION", pagination);
                commit("SET_REFUND_HISTORY_LOADED", true);
            } else {
                // Fallback for old format
                const data = Array.isArray(res.data) ? res.data : [];
                commit("SET_REFUND_HISTORY_ORDERS", data);
                commit("SET_REFUND_HISTORY_LOADED", true);
            }
        } catch (e) {
            console.error("Failed to fetch refund history:", e);
        } finally {
            // Always clear global loading state
            await dispatch('Search/setLoading', false, { root: true });
        }
    },

    async fetch_credit_slip_data({ state }, orderId) {
        return api.instance
            .get(`/api/order/fetch_refund_order_by_id/${orderId}`, {
            params: {
                mode: state.refund_fetch_mode,
            },
            })
            .then(res => res.data);
    },

    async fetch_order_details({ commit }, orderId) {
        commit('SET_LOADING_DETAILS', { orderId, loading: true });
        
        try {
        const response = await api.instance.get(`/api/order/${orderId}/details`);
        commit('SET_ORDER_DETAILS', { orderId, details: response.data.details });
        
        // Store order VAT rate and info for VAT calculations
        if (response.data.order_info && response.data.order_info.vat_rate !== undefined) {
            commit('SET_ORDER_VAT_RATE', { orderId, vatRate: response.data.order_info.vat_rate });
        }
        if (response.data.order_info) {
            commit('SET_ORDER_INFO', { orderId, orderInfo: response.data.order_info });
        }

        return response.data.details

        } catch (e) {
        console.error('Failed to fetch order details', e);
        } finally {
        commit('SET_LOADING_DETAILS', { orderId, loading: false });
        }
    },

    async fetch_order_notes(_context, orderId) {
        try {
            const response = await api.instance.get(`/api/order/fetch_refund_order_notes_by_id/${orderId}`);
            return response.data.notes || [];
        } catch (error) {
            console.error('Failed to fetch order notes:', error);
            throw error;
        }
    },

    async delete_order_note(_, noteId) {
        await api.instance.delete(`/api/order/delete_refund_order_note/${noteId}`);
    },

    async update_order_note(_, { id, feedback }) {
        await api.instance.put(`/api/order/update_refund_order_note/${id}`, { feedback });
    },

    //CLAIMS   
    async fetch_inventory_claims(_, { orderId = null, inventoryId = null }) {
        try {
            const params = {};
            if (orderId) params.order_id = orderId;
            if (inventoryId) params.inventory_id = inventoryId;

            const response = await api.instance.get('/api/inventory-claims/claims', { params });
            return response.data.claims;
        } catch (error) {
            console.error('Error fetching inventory claims:', error);
            throw error;
        }
    },

    
    async upload_claims_media(_context, { lineItemIds, file, onProgress }) {
        const formData = new FormData();
        formData.append('file', file);
        lineItemIds.forEach(id => formData.append('line_item_ids[]', id));

        try {
            const response = await api.instance.post('/api/inventory-claims/upload-claims-media', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                onUploadProgress: (progressEvent) => {
                    if (onProgress) {
                        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                        onProgress(percentCompleted);
                    }
                },
            });

            return response.data.media;
        } catch (error) {
            console.error('Failed to upload claims media:', error);
            throw error;
        }
    },

    async delete_claims_media(_context, { file_id, claim_id }) {
        try {
            await api.instance.delete('/api/inventory-claims/delete-claims-media', {
            data: {
                file_id,
                claim_id
            }
            });
        } catch (error) {
            console.error('Failed to delete claims media:', error);
            throw error;
        }
    },


    async fetch_claims_media(_context, payload) {
        try {
            const response = await api.instance.post(`/api/inventory-claims/fetch-claims-media` ,payload);
            return response.data.media_files || [];
        } catch (error) {
            console.error('Failed to fetch claims media:', error);
            throw error;
        }
    },
    // END OF CLAIMS FUNCTIONS


    clear_refund_order_details({ commit }) {
        commit("CLEAR_REFUND_ORDER_DETAILS");
    },

    clear_refundable_orders({ commit }) {
        commit('CLEAR_REFUNDABLE_ORDERS');
    },

    set_refund_history_loaded({ commit }, value) {
        commit("SET_REFUND_HISTORY_LOADED", value);
    },

    clear_refund_history_orders({commit})
    {
        commit("CLEAR_REFUND_HISTORY_ORDERS");
    },

    set_refund_fetch_mode({ commit }, mode) {
        commit("SET_REFUND_FETCH_MODE", mode);
    },
};

const getters = {
    refundable_orders: (state) => Array.isArray(state.refundable_orders) ? state.refundable_orders : [],
    refund_order_details: (state) => state.refund_order_details,
    refund_history_orders: (state) => state.refund_history_orders,
    refund_history_loaded: state => state.refund_history_loaded,
    refund_fetch_mode: (state) => state.refund_fetch_mode,
    getOrderDetails: (state) => (orderId) => state.orderDetailsCache[orderId] || [],
    isLoadingDetails: (state) => (orderId) => !!state.loadingDetails[orderId],
    getOrderVatRate: (state) => (orderId) => state.orderVatRates[orderId] || null,
    getOrderInfo: (state) => (orderId) => state.orderInfoCache[orderId] || null,
    // Pagination getters
    refundable_orders_pagination: (state) => state.refundable_orders_pagination,
    refund_history_pagination: (state) => state.refund_history_pagination,
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
};
